import {
    IDisclosuresActionsDetailsDto,
    IEnquiriesActionsDetailsDto,
    IRequestsUploadsActionsDetailListDto
} from '@vivli/features/dashboard/infrastructure/interface';
import { DashboardHeaderView } from '@vivli/features/dashboard/views';
import { PageBodyView } from '@vivli/shared/views';
import React, { CSSProperties, useEffect, useMemo } from 'react';
import { Size } from '@vivli/shared/theme';
import './dashboard-styles.scss';
import { ActionContentView } from '@vivli/features/dashboard/views';
import {
    DataRequestActionsGridComponent,
    DisclosureActionsGridComponent,
    EnquiryActionsGridComponent,
    UploadActionsGridComponent
} from '@vivli/features/dashboard/components';
import { IPageLink } from '@vivli/shared/infrastructure/interface';
import { GridApi } from 'ag-grid-community';
import { useAwaitingActionDetails, useIndividualAwaitingActionsHook } from '@vivli/features/dashboard/infrastructure/hook';
import { useConfirmLeaveDuringUpload } from '@vivli/core/infrastructure/hook';
import { useModalApiContext } from '@vivli/core/infrastructure/context';
import { first } from 'rxjs/operators';

const DATA_REQUESTS_TAB_URL = 'datarequests';

const containerStyle: CSSProperties = {
    width: '100%',
    minWidth: '1400px',
    paddingLeft: Size.PADDING / 2
};

const outerContainerStyle: CSSProperties = {
    display: 'grid',
    width: '100%',
    height: '100%',
    gridTemplateRows: 'auto 1fr',
    minWidth: '1200px',
    maxWidth: '98%',
    paddingLeft: Size.PADDING,
    paddingRight: Size.PADDING,
    marginLeft: 'auto',
    marginRight: 'auto'
};

/* eslint-disable-next-line */
export interface DashboardAdminViewProps {
    requestsUploadsActionsDto?: IRequestsUploadsActionsDetailListDto;
    enquiriesActionsDto?: IEnquiriesActionsDetailsDto;
    disclosuresActionsDto?: IDisclosuresActionsDetailsDto;
    headerLink?: string;
}

export const DashboardAdminComponent = ({
    requestsUploadsActionsDto,
    enquiriesActionsDto,
    disclosuresActionsDto,
    headerLink
}: DashboardAdminViewProps) => {
    const [currentRequestDto, setCurrentRequestDto] = React.useState<IRequestsUploadsActionsDetailListDto>(null);
    const { isVivliAdmin, canViewAnOrg, exportColumnKeys, studiesLabel, canViewOrgInfo, canViewEnquiryOrgInfo } =
        useAwaitingActionDetails();
    const [currentUploadCount, setCurrentUploadCount] = React.useState<number>(0);
    const { getRequestsAndUploads$ } = useIndividualAwaitingActionsHook();
    const modal = useModalApiContext();

    useEffect(() => {
        if (requestsUploadsActionsDto === null) {
            return;
        }
        setCurrentRequestDto(requestsUploadsActionsDto || null);
        setCurrentUploadCount(requestsUploadsActionsDto?.uploadActionsCount);
    }, [requestsUploadsActionsDto]);

    const drCount = useMemo(() => {
        return currentRequestDto?.dataRequestActionsCount;
    }, [currentRequestDto?.dataRequestActionsCount]);

    const enquiryCount = enquiriesActionsDto?.count || 0;
    const disclosureCount = disclosuresActionsDto?.count || 0;

    const synchUploadCount = (count: number) => {
        setCurrentUploadCount(count);
    };

    // define data request link up front for use in tabset
    // and as default
    const dataRequestLink: IPageLink = {
        title: 'Data Requests',
        count: drCount,
        url: DATA_REQUESTS_TAB_URL,
        component: (
            <ActionContentView
                count={drCount}
                actionDetails={currentRequestDto?.dataRequestActionDetails}
                noDataMainDescription={'There are no data requests to review or upload.'}
                noDataButtonText={'View all Data Requests'}
                noDataButtonUrl={'/admin/requests/AwaitingMyAction'}
                showGoToButton={canViewAnOrg}
                className={'data-request-action-card'}
            >
                <DataRequestActionsGridComponent
                    dtos={currentRequestDto?.dataRequestActionDetails}
                    isVivliAdmin={isVivliAdmin}
                    studiesLabel={studiesLabel}
                    onGridReady={(gridApi) => addGridApi(gridApi, DATA_REQUESTS_TAB_URL)}
                />
            </ActionContentView>
        )
    };
    const pageDefaultLink: IPageLink = dataRequestLink;

    const [activeGridLink, setActiveGridLink] = React.useState<IPageLink | null>(pageDefaultLink);
    const [gridApiList, setGridApiList] = React.useState<any>([]);

    // Dashboard tabs switch via local state (not the router), so the app-wide useBlocker guard can't
    // catch them. Prompt here before switching away while an upload is in progress. (DEV-485)
    const confirmLeaveDuringUpload = useConfirmLeaveDuringUpload();

    // Returning to the Data Requests tab refreshes its data. The grid used to get this for free by
    // refetching on every mount, which is what DEV-759 removed; doing it here costs one request per
    // tab activation rather than one per mount, and refreshes the tab count along with the rows.
    const refreshDataRequestActions = () => {
        getRequestsAndUploads$()
            ?.pipe(first())
            .subscribe({
                next: (refreshed) => {
                    setCurrentRequestDto(refreshed);
                    setCurrentUploadCount(refreshed?.uploadActionsCount);
                },
                error: (error: any) => {
                    modal.error(error?.message ?? 'Error refreshing data request actions.');
                }
            });
    };

    const onActiveLinkChange = (link: IPageLink) => {
        setActiveGridLink(link);

        if (link?.url === DATA_REQUESTS_TAB_URL) {
            refreshDataRequestActions();
        }
    };

    const handleExport = () => {
        exportGridData(activeGridLink);
    };

    const gridApis: { gridApi: GridApi; id: string }[] = [];

    const exportGridData = (tab: IPageLink) => {
        const gridApi = gridApiList.find((ga) => ga.id === tab.url)?.gridApi;
        const date = new Date();
        const dateStr = `${String(date.getDate()).padStart(2, '0')}${String(date.getMonth() + 1).padStart(2, '0')}${date.getFullYear()}`;
        const titleStr = tab.url.replace(/\s+/g, '_').toLowerCase();
        const fileName = `${titleStr}_actions_awaiting_${dateStr}`;

        const keys = exportColumnKeys.find((eck) => eck.url === tab.url)?.keys;
        gridApi.exportDataAsCsv({ fileName, columnKeys: keys });
    };

    const addGridApi = (gridApi: GridApi, url) => {
        gridApis.push({ gridApi, id: url });
        setGridApiList(gridApis);
    };

    return (
        <div className="open-sans" style={outerContainerStyle}>
            <DashboardHeaderView
                moreInformationDescription={'Vivli Organization Administrator and Data Upload Guide'}
                moreInformationUrl={headerLink}
                handleExport={handleExport}
                currentLink={activeGridLink}
                showOrgButton={canViewOrgInfo}
            />
            <div style={containerStyle}>
                <PageBodyView
                    horizontalTabs={true}
                    links={[
                        dataRequestLink,
                        {
                            title: 'Disclosures',
                            count: disclosureCount,
                            url: 'disclosures',
                            component: (
                                <ActionContentView
                                    count={disclosureCount}
                                    actionDetails={requestsUploadsActionsDto?.uploadActionDetails}
                                    noDataMainDescription={'There are no disclosures to review.'}
                                    noDataButtonText={''}
                                    className={'disclosure-action-card'}
                                >
                                    <DisclosureActionsGridComponent
                                        dtos={disclosuresActionsDto?.disclosureActionDetailDtos}
                                        onGridReady={(gridApi) => addGridApi(gridApi, 'disclosures')}
                                    />
                                </ActionContentView>
                            )
                        },
                        {
                            title: 'Data Uploads',
                            count: currentUploadCount,
                            url: 'uploads',
                            component: (
                                <ActionContentView
                                    count={currentUploadCount}
                                    actionDetails={requestsUploadsActionsDto?.uploadActionDetails}
                                    noDataMainDescription={'There are no data uploads to complete.'}
                                    noDataButtonText={''}
                                    className={'upload-action-card'}
                                >
                                    <UploadActionsGridComponent
                                        dtos={requestsUploadsActionsDto?.uploadActionDetails}
                                        rowHeight={120}
                                        onGridReady={(gridApi) => addGridApi(gridApi, 'uploads')}
                                        synchUploadCount={synchUploadCount}
                                    />
                                </ActionContentView>
                            )
                        },
                        {
                            title: 'Enquiries',
                            count: enquiryCount,
                            url: 'enquiries',
                            component: (
                                <ActionContentView
                                    count={enquiryCount}
                                    actionDetails={enquiriesActionsDto?.enquiryActionsDetailsDtos}
                                    noDataMainDescription={'There are no enquiries to review.'}
                                    noDataButtonText={'View all Enquiries'}
                                    noDataButtonUrl={'/admin/enquiries/'}
                                    showGoToButton={canViewEnquiryOrgInfo}
                                    className={'enquiry-action-card'}
                                >
                                    <EnquiryActionsGridComponent
                                        dtos={enquiriesActionsDto?.enquiryActionsDetailsDtos}
                                        onGridReady={(gridApi) => addGridApi(gridApi, 'enquiries')}
                                        isVivliAdmin={isVivliAdmin}
                                        studiesLabel={studiesLabel}
                                    />
                                </ActionContentView>
                            )
                        }
                    ]}
                    defaultLink={dataRequestLink}
                    onActiveLinkChange={onActiveLinkChange}
                    beforeTabChange={() => confirmLeaveDuringUpload()}
                />
            </div>
        </div>
    );
};
